import { useRef, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import {
  FiChevronDown,
  FiArrowUp,
  FiArrowDown,
  FiCheckCircle,
  FiCheckSquare,
  FiLoader,
  FiXCircle,
} from 'react-icons/fi';
import { MdModeEdit } from 'react-icons/md';
import { TbSquareXFilled } from 'react-icons/tb';
import { FaGear, FaMagnifyingGlassChart } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import Swal from 'sweetalert2';

import api from '../../Services/api';
import './accordionSecao.css';

const AccordionSecao = ({
  secao,
  operador,
  isOpen,
  onToggle,
  onEdit,
  isReordenando = false,
  onMoveUp,
  onMoveDown,
  disableMoveUp = false,
  disableMoveDown = false,
  onSituacaoChanged,
}) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);
  const heightRef = useRef(0);
  const rafRef = useRef(null);
  const navigate = useNavigate();
  const [situacao, setSituacao] = useState(secao.situacao ?? 1);
  const [updatingSituacao, setUpdatingSituacao] = useState(false);

  const total = secao.qntQuestoes || 0;
  const respondidas = secao.qntRespostas || 0;
  const pendentes = Math.max(total - respondidas, 0);
  const inativasTotal = Number(secao.qntQuestoesInativas || 0);
  const inativasRespondidas = Number(secao.qntRespostasInativas || 0);
  const inativasPendentes = Number(secao.qntPendentesInativas || 0);
  const hasInativas =
    inativasTotal > 0 || inativasRespondidas > 0 || inativasPendentes > 0;
  const possuiFilhas = Number(secao.qntSubSecoes || 0) > 0;
  const totalAtivas = Math.max(total - inativasTotal, 0);
  const respondidasAtivas = Math.max(respondidas - inativasRespondidas, 0);
  const percentual =
    totalAtivas > 0 ? Math.round((respondidasAtivas / totalAtivas) * 100) : 0;
  const isFinalizado = situacao === 2;
  const isDesativado = situacao === 0;
  const situacaoLabel = isFinalizado
    ? 'Finalizado'
    : isDesativado
      ? 'Ativar'
      : 'Desativar';
  const situacaoTooltip = isFinalizado
    ? 'Finalizado'
    : isDesativado
      ? 'Desativado'
      : 'Em andamento';
  const situacaoIcon = isFinalizado ? (
    <FiCheckCircle />
  ) : isDesativado ? (
    <FiXCircle />
  ) : (
    <FiLoader />
  );
  const situacaoClass = isFinalizado
    ? 'acc-situacao-finalizado'
    : isDesativado
      ? 'acc-situacao-desativado'
      : 'acc-situacao-ativo';

  

  /* =========================
     ESTADO DA SEÇÃO
  ========================== */
  const hasCargo = useMemo(() => {
  const cargos = operador?.cargos ?? [];
  return (nomeCargo) => cargos.some((c) => c.descricao === nomeCargo);
  }, [operador]);

  const isAdmin = useMemo(() => {
    return (
      operador?.permissaoPrincipal === 99 ||
      hasCargo("Administrador")
    );
  }, [operador, hasCargo]);

  const isGerenteAuditoria = useMemo(() => {
    return hasCargo("Gerente Auditoria");
  }, [hasCargo]);

  const isAuditoriaResponde = useMemo(() => {
    return hasCargo("Auditoria - Responde Auditoria, sobe arquivos, mas sem acesso ao menu de Cadastro");
  }, [hasCargo]);

  // ✅ Regras finais por botão:
  const canManageSecao = useMemo(() => {
    // Editar / Desativar / Gerenciar
    return isAdmin || isGerenteAuditoria;
  }, [isAdmin, isGerenteAuditoria]);

  const canResponder = useMemo(() => {
    // Responder: Admin (99) OU (permissaoPrincipal=5) nos cargos abaixo
    if (isAdmin) return true;
    if (operador?.permissaoPrincipal !== 5) return false;
    return isGerenteAuditoria || isAuditoriaResponde;
  }, [isAdmin, operador, isGerenteAuditoria, isAuditoriaResponde]);


  /* =========================
     ESTADO DA SEÇÃO
  ========================== */
  const estado = useMemo(() => {
    if (total === 0) {
      return {
        classe: 'estado-sem-questoes',
        label: 'Nenhuma questão cadastrada',
      };
    }

    if (respondidas === 0) {
      return {
        classe: 'estado-nao-iniciado',
        label: 'Não iniciado',
      };
    }

    if (respondidas < total) {
      return {
        classe: 'estado-em-andamento',
        label: 'Em andamento',
      };
    }

    if (respondidas === total) {
      return {
        classe: 'estado-completo',
        label: 'Totalmente preenchido',
      };
    }

    return {
      classe: 'estado-cadastrada',
      label: 'Seção cadastrada',
    };
  }, [total, respondidas]);



  /* =========================
     CONTROLE DE ALTURA
  ========================== */
  useLayoutEffect(() => {
    if (!contentRef.current) return;
    if (!isOpen) {
      heightRef.current = 0;
      setHeight(0);
      return;
    }

    const el = contentRef.current;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const nextHeight = el.scrollHeight;
      if (heightRef.current !== nextHeight) {
        heightRef.current = nextHeight;
        setHeight(nextHeight);
      }
      rafRef.current = null;
    });
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isOpen, situacao, total, respondidas]);

  useEffect(() => {
    setSituacao(secao.situacao ?? 1);
  }, [secao.situacao]);

  const handleToggleSituacao = async (e) => {
    e.stopPropagation();

    if (!canManageSecao) return;

    if (isFinalizado || updatingSituacao) return;

    const nextSituacao = isDesativado ? 1 : 0;
    const actionLabel = isDesativado ? 'Ativar' : 'Desativar';
    const confirmResult = await Swal.fire({
      title: `${actionLabel} se\u00e7\u00e3o?`,
      text: `Confirma ${actionLabel.toLowerCase()} a se\u00e7\u00e3o "${secao.descricao}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Sim, ${actionLabel.toLowerCase()}`,
      cancelButtonText: 'Cancelar',
      cancelButtonColor: "var(--color-btnCancelar-danger)",
      customClass: {
        cancelButton: "swal-cancel-danger",
      },
    });

    if (!confirmResult.isConfirmed) return;

    const payload = {
      IDSecao: secao.IDSecao,
      IDCliente: secao.IDCliente,
      IDSecaoPai: secao.IDSecaoPai ?? null,
      descricao: secao.descricao,
      ordem: secao.ordem ?? null,
      situacao: nextSituacao,
    };

    try {
      setUpdatingSituacao(true);
      await api.put(`/chksecao/${secao.IDSecao}`, payload);
      setSituacao(nextSituacao);
      if (onSituacaoChanged) {
        await onSituacaoChanged();
      }
    } catch (error) {
      console.error('Erro ao atualizar a situacao da secao', error);
    } finally {
      setUpdatingSituacao(false);
    }
  };

  return (
    <div className={`acc-row ${isReordenando ? 'reorder-active' : ''}`}>
      {isReordenando && (
        <div className="acc-reorder-slot">
          <button
            className="acc-reorder-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (onMoveUp) onMoveUp();
            }}
            disabled={disableMoveUp}
          >
            <FiArrowUp />
          </button>
          <button
            className="acc-reorder-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (onMoveDown) onMoveDown();
            }}
            disabled={disableMoveDown}
          >
            <FiArrowDown />
          </button>
        </div>
      )}
      <div
        className={`acc-item ${estado.classe} ${
          isDesativado ? 'situacao-desativado' : ''
        } ${isFinalizado ? 'situacao-finalizado' : ''} ${isOpen ? 'open' : ''}`}
      >
        {/* ===== CABEÇALHO ===== */}
        <div
          className={`acc-header ${isReordenando ? 'reorder-mode' : ''}`}
          onClick={() => {
            if (!isReordenando && onToggle) onToggle();
          }}
        >
          {/* ESQUERDA */}
          <div className="acc-left">
            <div className="acc-ordem-box">
              <span className="acc-ordem">{secao.ordem}</span>
            </div>

            <strong className="acc-title">
              {secao.descricao}
              {possuiFilhas ? " (Grupo)" : ""}
            </strong>
          </div>

          <div className="acc-progress-bar">
            <div
              className="acc-progress-bar-fill"
              style={{ width: `${percentual}%` }}
              aria-label={`Progresso ${percentual}%`}
            />
          </div>
          <span style={{ fontSize: 10 }}>{`${percentual}%`}</span>

          {/* DIREITA */}
          <div className="acc-right">
            <span className="acc-date-label">Atualizado em:</span>
            <span className="acc-date-value">
              {new Date(secao.dataAlteracao).toLocaleDateString('pt-BR')} -{' '}
              {new Date(secao.dataAlteracao).toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            <span
              className={`acc-situacao-icon ${situacaoClass}`}
              title={situacaoTooltip}
            >
              {situacaoIcon}
            </span>
          </div>

          <div className="acc-icon">
            <FiChevronDown />
          </div>
        </div>

        {/* ===== CONTEÚDO ===== */}
        <div className="acc-wrapper" style={{ height: `${height}px` }}>
          <div className="acc-content" ref={contentRef}>
            <div className="acc-layout">
              {/* COLUNA ESQUERDA */}
              <div className="acc-col acc-col-left">
                <span className="badge-situacao">{estado.label}</span>

                <div className="acc-stats-row">
                  <div className="progress-wrapper">
                    <CircularProgressbar
                      value={percentual}
                      text={`${percentual}%`}
                      styles={buildStyles({
                        textSize: '22px',
                        pathColor: '#1171d2',
                        textColor: '#111827',
                        trailColor: '#e5e7eb',
                      })}
                    />
                  </div>

                  <div className="stats-grid">
                    <div className="stat-card">
                      <span className="stat-value">{total}</span>
                      <span className="stat-label">Questões</span>
                      {hasInativas && (
                        <span className="stat-label-sub">
                          {inativasTotal > 0 ? `(${inativasTotal} inativas)` : ""}
                        </span>
                      )}
                    </div>

                    <div className="stat-card success">
                      <span className="stat-value">{respondidas}</span>
                      <span className="stat-label">Respondidas</span>
                      {hasInativas && (
                        <span className="stat-label-sub">
                          {inativasRespondidas > 0
                            ? `(${inativasRespondidas} inativas)`
                            : ""}
                        </span>
                      )}
                    </div>

                    <div className="stat-card danger">
                      <span className="stat-value">{pendentes}</span>
                      <span className="stat-label">Pendentes</span>
                      {hasInativas && (
                        <span className="stat-label-sub">
                          {inativasPendentes > 0
                            ? `(${inativasPendentes} inativas)`
                            : ""}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* COLUNA DIREITA */}
              <div className="acc-col acc-col-right">
                {canManageSecao && (
                  <button
                    className="btn-icon btn-edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(secao.IDSecao);
                    }}
                    disabled={isDesativado || isFinalizado}
                  >
                    <MdModeEdit /> Editar
                  </button>
                )}


                {canManageSecao && (
                  <button
                    className={`btn-icon ${
                      isFinalizado
                        ? "btn-success"
                        : isDesativado
                          ? "btn-activate"
                          : "btn-delete"
                    }`}
                    onClick={handleToggleSituacao}
                    disabled={isFinalizado || updatingSituacao}
                  >
                    {isDesativado || isFinalizado ? <FiCheckCircle /> : <TbSquareXFilled />}{" "}
                    {situacaoLabel}
                  </button>
                )}


                <button
                  className="btn-icon btn-view"
                  onClick={() =>
                    navigate(`/Auditoria/${secao.IDSecao}/Acompanhamento`)
                  }
                  disabled={total === 0 || possuiFilhas}
                >
                  <FaMagnifyingGlassChart /> Visualizar
                </button>

                {canManageSecao && (
                  <button
                    className="btn-icon btn-manager"
                    onClick={() => navigate(`/AuditoriaQuestao/${secao.IDSecao}/0`)}
                    disabled={isDesativado || possuiFilhas}
                  >
                    <FaGear /> Gerenciar
                  </button>
                )}

                {canResponder && (
                  <button
                    className="btn-icon btn-respond"
                    onClick={() => navigate(`/AuditoriaChecklist/${secao.IDSecao}`)}
                    disabled={total === 0 || isDesativado || isFinalizado || possuiFilhas}
                  >
                    <FiCheckSquare /> Responder
                  </button>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccordionSecao;
